/**
 * SOUND CUES HOOK
 *
 * Audio feedback for warehouse scanning operations.
 * Uses Web Audio API for instant, synthesized tones.
 */

import { useCallback, useRef } from 'react';

interface UseSoundOptions {
  enabled?: boolean;
  volume?: number;
}

interface UseSoundReturn {
  playSuccess: () => void;
  playError: () => void;
  playWarning: () => void;
  playBeep: () => void;
  playCustom: (frequency: number, duration: number, type?: OscillatorType) => void;
  isSupported: boolean;
}

// Audio context singleton to avoid creating multiple contexts
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      console.warn('Web Audio API not supported');
      return null;
    }
  }

  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }

  return audioContext;
}

function playTone(
  frequency: number,
  duration: number,
  volume: number = 0.3,
  type: OscillatorType = 'sine'
): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

  // Envelope: quick attack, sustain, quick release
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
  gainNode.gain.setValueAtTime(volume, ctx.currentTime + duration - 0.05);
  gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

function playSuccessTone(volume: number): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Two-tone ascending melody (C5 -> E5)
  playTone(523.25, 0.1, volume, 'sine'); // C5
  setTimeout(() => {
    playTone(659.25, 0.15, volume, 'sine'); // E5
  }, 100);
}

function playErrorTone(volume: number): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Low buzzing tone with slight wobble
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(220, ctx.currentTime);
  osc.frequency.linearRampToValueAtTime(180, ctx.currentTime + 0.3);

  gain.gain.setValueAtTime(0, ctx.currentTime);
  gain.gain.linearRampToValueAtTime(volume * 0.5, ctx.currentTime + 0.02);
  gain.gain.setValueAtTime(volume * 0.5, ctx.currentTime + 0.25);
  gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.3);
}

function playWarningTone(volume: number): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Medium-pitched beep-beep pattern
  playTone(440, 0.1, volume, 'triangle'); // A4
  setTimeout(() => {
    playTone(440, 0.1, volume, 'triangle'); // A4 again
  }, 150);
}

function playBeepTone(volume: number): void {
  // Simple single beep for scanning
  playTone(880, 0.08, volume, 'sine'); // A5 - high, short
}

/**
 * Hook for playing audio feedback sounds in warehouse operations.
 *
 * @example
 * const { playSuccess, playError, playWarning } = useSound();
 *
 * // On successful scan
 * playSuccess();
 *
 * // On error (wrong item, etc.)
 * playError();
 *
 * // On warning (low stock, etc.)
 * playWarning();
 */
export function useSound(options: UseSoundOptions = {}): UseSoundReturn {
  const { enabled = true, volume = 0.3 } = options;
  const lastPlayTime = useRef<number>(0);

  // Debounce to prevent sound spam
  const canPlay = useCallback(() => {
    const now = Date.now();
    if (now - lastPlayTime.current < 100) return false;
    lastPlayTime.current = now;
    return enabled;
  }, [enabled]);

  const playSuccess = useCallback(() => {
    if (!canPlay()) return;
    playSuccessTone(volume);
  }, [canPlay, volume]);

  const playError = useCallback(() => {
    if (!canPlay()) return;
    playErrorTone(volume);
  }, [canPlay, volume]);

  const playWarning = useCallback(() => {
    if (!canPlay()) return;
    playWarningTone(volume);
  }, [canPlay, volume]);

  const playBeep = useCallback(() => {
    if (!canPlay()) return;
    playBeepTone(volume);
  }, [canPlay, volume]);

  const playCustom = useCallback((
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine'
  ) => {
    if (!canPlay()) return;
    playTone(frequency, duration, volume, type);
  }, [canPlay, volume]);

  const isSupported = typeof window !== 'undefined' &&
    !!(window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);

  return {
    playSuccess,
    playError,
    playWarning,
    playBeep,
    playCustom,
    isSupported,
  };
}

export default useSound;
